import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

/*
 * The shell's chrome is owned by the screen that is showing. A screen calls
 * `useShell()` and sets the header, the bottom action bar and the focused
 * flag from an effect. Every value is stamped with the pathname it was set
 * on; AppShell only honours values whose pathname matches the current route,
 * so nothing a screen set can leak into the next screen (the next screen
 * simply has not set anything yet → defaults).
 *
 *   setHeader({ title, subtitle, chevron })  — the MobHeader. `chevron` makes
 *                                              the title a button (order menu)
 *   setActions(node | null)                  — rendered in `.app-shell__actions`,
 *                                              directly above the tab bar area.
 *                                              Screens render their own
 *                                              div.wtr-actions / wtr-addbar /
 *                                              wtr-cart inside the node
 *   setFocused(bool)                         — hides the tab bar (a focused
 *                                              task: seating, order, check)
 */

export interface ShellHeader {
  title: string;
  subtitle?: string;
  /** When set, the title renders as a button with the ▼ chevron. */
  chevron?: { onClick: () => void };
}

export interface ShellApi {
  setHeader: (header: ShellHeader | null) => void;
  setActions: (node: ReactNode | null) => void;
  setFocused: (focused: boolean) => void;
}

interface Stamped<T> {
  pathname: string;
  value: T;
}

export interface ShellState {
  header: ShellHeader | null;
  actions: ReactNode | null;
  focused: boolean;
}

const ApiCtx = createContext<ShellApi | null>(null);
const StateCtx = createContext<ShellState | null>(null);

export function ShellProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  const [header, setHeaderRaw] = useState<Stamped<ShellHeader | null> | null>(null);
  const [actions, setActionsRaw] = useState<Stamped<ReactNode | null> | null>(null);
  const [focused, setFocusedRaw] = useState<Stamped<boolean> | null>(null);

  const setHeader = useCallback((value: ShellHeader | null) => {
    setHeaderRaw({ pathname: pathRef.current, value });
  }, []);
  const setActions = useCallback((value: ReactNode | null) => {
    setActionsRaw({ pathname: pathRef.current, value });
  }, []);
  const setFocused = useCallback((value: boolean) => {
    setFocusedRaw({ pathname: pathRef.current, value });
  }, []);

  const api = useMemo<ShellApi>(() => ({ setHeader, setActions, setFocused }), [setHeader, setActions, setFocused]);

  const state = useMemo<ShellState>(
    () => ({
      header: header && header.pathname === pathname ? header.value : null,
      actions: actions && actions.pathname === pathname ? actions.value : null,
      focused: focused && focused.pathname === pathname ? focused.value : false,
    }),
    [header, actions, focused, pathname],
  );

  return (
    <ApiCtx.Provider value={api}>
      <StateCtx.Provider value={state}>{children}</StateCtx.Provider>
    </ApiCtx.Provider>
  );
}

/** The screen-side API. Stable functions — safe in effect deps. */
export function useShell(): ShellApi {
  const ctx = useContext(ApiCtx);
  if (!ctx) throw new Error('useShell must be used inside <ShellProvider>');
  return ctx;
}

/** The shell-side state (AppShell reads this). */
export function useShellState(): ShellState {
  const ctx = useContext(StateCtx);
  if (!ctx) throw new Error('useShellState must be used inside <ShellProvider>');
  return ctx;
}
